import { useEffect, useRef, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Camera, LogIn, MapPin, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { extraApi } from '@/lib/extraApi';
import { useAuth } from '@/context/AuthContext';

const STAFF_ROLES = ['sales', 'installation', 'care', 'vendor'];

/**
 * On first login of each calendar day, staff must punch IN before using the app.
 * Renders a full-screen non-dismissible dialog until the user records a Punch IN.
 *
 * How it works:
 * - Runs only for staff roles (sales / installation / care / vendor).
 * - Checks GET /attendance/me → if `today_in` is null and user hasn't dismissed for today, the dialog stays open.
 * - Requires selfie + geolocation before the POST /attendance/punch call, mirroring the standard StaffAttendance form.
 * - Once punch succeeds, the dialog closes and the app becomes usable.
 * - `localStorage['att_gate_last_check']` keeps a per-day session flag so we don't hit the API on every route change.
 */
export default function AttendanceGate() {
    const { user } = useAuth();
    const [needsPunch, setNeedsPunch] = useState(false);
    const [checking, setChecking] = useState(false);
    const [busy, setBusy] = useState(false);
    const [selfie, setSelfie] = useState('');
    const [loc, setLoc] = useState(null);
    const [locErr, setLocErr] = useState('');
    const [note, setNote] = useState('');
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    const isStaff = !!user && STAFF_ROLES.includes(user.role);

    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD (UTC-ish, close enough for a daily gate)

    const stopCamera = useCallback(() => {
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    }, []);

    const check = useCallback(async () => {
        if (!isStaff) return;
        setChecking(true);
        try {
            // Phase 42 — respect the admin-controlled enforcement toggle.
            // If admin has turned OFF daily attendance, do NOT show the gate.
            let gateEnforced = true;
            try {
                const es = await extraApi.attendanceEnforcementStatus();
                gateEnforced = !!(es?.attendance_enforcement_enabled);
            } catch {
                // Server can't answer → fail-safe: assume enforced (keeps
                // existing behaviour for offline / older backends).
                gateEnforced = true;
            }
            if (!gateEnforced) {
                setNeedsPunch(false);
                // Cache so we don't re-hit the endpoint on every route change.
                localStorage.setItem('att_gate_last_check', today);
                return;
            }
            const data = await extraApi.myAttendance();
            const punchedToday = !!data?.today_in;
            setNeedsPunch(!punchedToday);
            if (punchedToday) {
                localStorage.setItem('att_gate_last_check', today);
            }
        } catch (e) {
            // On network failure, don't block the app — soft fail
            setNeedsPunch(false);
        } finally { setChecking(false); }
    }, [isStaff, today]);

    // Check on mount + whenever the user identity changes
    useEffect(() => {
        if (!isStaff) return;
        const cached = localStorage.getItem('att_gate_last_check');
        // If we already saw a punch-in today in this session, skip network entirely
        if (cached === today) { setNeedsPunch(false); return; }
        check();
    }, [isStaff, today, check]);

    // Cleanup camera on unmount / dialog close
    useEffect(() => { return () => stopCamera(); }, [stopCamera]);

    const getLocation = () => {
        setLocErr('');
        if (!navigator.geolocation) {
            setLocErr('Geolocation not supported');
            fallbackToIp('Browser does not support GPS');
            return;
        }
        // Phase 48 — GPS timeout on some Android WebViews takes 30+ seconds.
        // Kick off the browser API AND arm our own 15-second watchdog. If
        // GPS answers first, great — we tag `source: 'gps'`. Otherwise the
        // watchdog fires an IP-based fallback so Punch IN is never blocked.
        let resolved = false;
        const watchdog = setTimeout(() => {
            if (resolved) return;
            resolved = true;
            fallbackToIp('GPS took too long — using approximate location');
        }, 15000);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                if (resolved) return;
                resolved = true;
                clearTimeout(watchdog);
                setLoc({
                    latitude:  pos.coords.latitude,
                    longitude: pos.coords.longitude,
                    accuracy:  pos.coords.accuracy,
                    source:    'gps',
                });
            },
            () => {
                if (resolved) return;
                resolved = true;
                clearTimeout(watchdog);
                fallbackToIp('Location permission denied — using approximate location from IP');
            },
            { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
        );
    };

    const fallbackToIp = async (userMessage) => {
        try {
            const r = await extraApi.ipLocate();
            if (r && r.ok && Number.isFinite(r.latitude) && Number.isFinite(r.longitude)) {
                setLoc({
                    latitude:  r.latitude,
                    longitude: r.longitude,
                    accuracy:  r.accuracy || 25000,
                    source:    'ip',
                    city:      r.city || null,
                });
                setLocErr('');
                toast.warning(userMessage || 'Using approximate location from IP');
                return;
            }
        } catch { /* fall through */ }
        setLocErr('Could not determine your location. Enable GPS and retry.');
    };

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
            streamRef.current = stream;
            if (videoRef.current) videoRef.current.srcObject = stream;
        } catch (e) { toast.error('Camera permission denied'); }
    };
    const captureSelfie = () => {
        const v = videoRef.current;
        if (!v || !v.videoWidth) { toast.error('Start camera first'); return; }
        const c = document.createElement('canvas');
        c.width = 480; c.height = Math.round(480 * v.videoHeight / v.videoWidth);
        c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
        setSelfie(c.toDataURL('image/jpeg', 0.7));
        stopCamera();
    };

    const doPunch = async () => {
        if (!selfie) { toast.error('Please capture a selfie first'); return; }
        if (!loc) { toast.error('Please allow location access'); return; }
        setBusy(true);
        try {
            // Phase 47 — defensive coords. WebView geolocation on some Android
            // devices returns non-finite values which crashed `.toFixed(5)`
            // and left the screen blank after the Punch IN click.
            const lat = Number.isFinite(loc.latitude)  ? Number(loc.latitude)  : 0;
            const lng = Number.isFinite(loc.longitude) ? Number(loc.longitude) : 0;
            const acc = Number.isFinite(loc.accuracy)  ? Number(loc.accuracy)  : null;
            await extraApi.punchAttendance({
                kind: 'in',
                selfie_base64: selfie,
                latitude:  lat,
                longitude: lng,
                accuracy:  acc,
                address:   `Lat ${lat.toFixed(5)}, Lng ${lng.toFixed(5)}${loc.city ? ' • ' + loc.city : ''}`,
                note,
                location_source: loc.source || 'gps',
            });
            toast.success('Punched IN — welcome! You can now use the app.');
            try { localStorage.setItem('att_gate_last_check', today); } catch { /* private mode */ }
            setNeedsPunch(false);
            try { stopCamera(); } catch { /* ignore — camera may already be off */ }
        } catch (e) {
            toast.error(e?.friendlyMessage || e?.response?.data?.detail || 'Punch failed. Try again.');
            // eslint-disable-next-line no-console
            console.error('[AttendanceGate] punch failed:', e);
        } finally { setBusy(false); }
    };

    if (!isStaff || checking || !needsPunch) return null;

    return (
        <Dialog open={true} onOpenChange={() => { /* not dismissible until punch */ }} modal>
            <DialogContent
                className='max-w-lg max-h-[92vh] overflow-y-auto [&>button.absolute]:hidden'
                onEscapeKeyDown={(e) => e.preventDefault()}
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                data-testid='attendance-gate-dialog'
            >
                <DialogHeader>
                    <div className='mx-auto h-12 w-12 rounded-full bg-primary/10 grid place-items-center mb-2'>
                        <ShieldCheck className='w-6 h-6 text-primary' />
                    </div>
                    <DialogTitle className='text-center'>Punch In to Continue / जारी रखने के लिए पंच इन करें</DialogTitle>
                    <DialogDescription className='text-center text-sm'>
                        कृपया आज का <b>Punch IN</b> पूरा करें ताकि आप CRM का उपयोग कर सकें। एक selfie + location capture ज़रूरी है।
                    </DialogDescription>
                </DialogHeader>

                <div className='space-y-3'>
                    {/* Location */}
                    <div className='rounded-md border bg-muted/30 p-3 space-y-1' data-testid='att-gate-location'>
                        <Label className='text-xs uppercase tracking-wide text-muted-foreground'>Location / स्थान</Label>
                        {loc ? (
                            <div className='text-sm inline-flex items-center gap-1 text-emerald-600'>
                                <MapPin className='w-4 h-4' /> Captured — Lat {Number(loc.latitude || 0).toFixed(5)}, Lng {Number(loc.longitude || 0).toFixed(5)}
                                {loc.source === 'ip' && (
                                    <span className='ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 border border-amber-400/50' data-testid='ip-fallback-badge'>
                                        IP fallback {loc.city ? `• ${loc.city}` : ''}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <div className='flex items-center justify-between'>
                                <span className='text-sm text-muted-foreground'>{locErr || 'Not captured yet'}</span>
                                <Button size='sm' variant='outline' onClick={getLocation} data-testid='att-gate-get-location'>
                                    <MapPin className='w-3.5 h-3.5 mr-1' /> Get Location
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Selfie */}
                    <div className='rounded-md border bg-muted/30 p-3 space-y-2' data-testid='att-gate-selfie'>
                        <Label className='text-xs uppercase tracking-wide text-muted-foreground'>Selfie / सेल्फी</Label>
                        {selfie ? (
                            <div className='flex items-center gap-2'>
                                <img src={selfie} alt='selfie' className='w-20 h-20 rounded object-cover border' />
                                <Button size='sm' variant='outline' onClick={() => { setSelfie(''); startCamera(); }}>Retake</Button>
                            </div>
                        ) : (
                            <>
                                <video ref={videoRef} autoPlay playsInline className='w-full rounded max-h-56 bg-black' />
                                <div className='flex gap-2'>
                                    <Button size='sm' variant='outline' onClick={startCamera} data-testid='att-gate-start-camera'>
                                        <Camera className='w-3.5 h-3.5 mr-1' /> Start Camera
                                    </Button>
                                    <Button size='sm' onClick={captureSelfie} data-testid='att-gate-capture'>Capture</Button>
                                </div>
                            </>
                        )}
                    </div>

                    <div className='space-y-1'>
                        <Label className='text-xs'>Note (optional) / टिप्पणी</Label>
                        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder='e.g. Working from Kanpur office' data-testid='att-gate-note' />
                    </div>

                    <Button
                        className='w-full'
                        disabled={busy || !selfie || !loc}
                        onClick={doPunch}
                        data-testid='att-gate-punch-in'
                    >
                        {busy ? <Loader2 className='w-4 h-4 mr-2 animate-spin' /> : <LogIn className='w-4 h-4 mr-2' />}
                        {busy ? 'Punching In…' : 'Punch IN & Continue'}
                    </Button>
                    <div className='text-[11px] text-center text-muted-foreground'>
                        यह dialog दिन में सिर्फ एक बार दिखेगा — punch होने के बाद पूरा CRM use कर सकते हैं।
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
